import { computed, onMounted, ref, watch, type Ref } from 'vue';

import { ensureFreshKeycloakToken } from '@apexx/auth';
import type { OverviewMetric } from '@apexx/component-library';
import type { AppRegistryEntry } from '@apexx/shell-contract';

function toYmd(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

/** Monday-based week start (local time). */
function startOfWeekMonday(date: Date): Date {
	const d = new Date(date);
	const day = d.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	d.setDate(d.getDate() + diff);
	d.setHours(0, 0, 0, 0);
	return d;
}

export type LauncherStatusWidget = OverviewMetric & {
	loading: boolean;
};

type AccessibleApps = Ref<AppRegistryEntry[]>;

function apiBase(envKey: string): string {
	const raw = import.meta.env[envKey] as string | undefined;
	return (raw ?? '').replace(/\/$/, '');
}

async function authHeaders(): Promise<HeadersInit> {
	const token = (await ensureFreshKeycloakToken()) ?? '';
	return {
		Accept: 'application/json',
		Authorization: `Bearer ${token}`,
	};
}

async function getJson<T>(url: string): Promise<T | null> {
	try {
		const res = await fetch(url, { headers: await authHeaders() });
		if (!res.ok) {
			return null;
		}
		return (await res.json()) as T;
	} catch {
		return null;
	}
}

function hasApp(apps: AppRegistryEntry[], id: string): boolean {
	return apps.some((app) => app.id === id && !app.disabled);
}

function completedStatus(delta: number | null): string {
	if (delta == null) {
		return 'Compared to last week';
	}
	if (delta === 0) {
		return 'Same as last week';
	}
	if (delta > 0) {
		return `${delta} more than last week`;
	}
	return `${Math.abs(delta)} fewer than last week`;
}

export function useLauncherStatus(accessibleApps: AccessibleApps) {
	const loading = ref(true);

	const unreadMessages = ref<number | null>(null);
	const unreadChannels = ref(0);
	const tasksDueToday = ref<number | null>(null);
	const tasksOverdue = ref(0);
	const completedThisWeek = ref<number | null>(null);
	const completedLastWeek = ref<number | null>(null);
	const machinesNeedingAttention = ref<number | null>(null);
	const machinesCritical = ref(0);
	const documentsUnread = ref<number | null>(null);
	const openInspections = ref<number | null>(null);

	const widgets = computed((): LauncherStatusWidget[] => {
		const apps = accessibleApps.value;
		const items: LauncherStatusWidget[] = [];

		if (hasApp(apps, 'chat')) {
			const unread = unreadMessages.value;
			const isAlert = (unread ?? 0) > 0;
			items.push({
				id: 'unread-messages',
				appId: 'chat',
				icon: 'messageSquare',
				label: 'Unread chat messages',
				status:
					unread == null
						? 'Loading…'
						: unread === 0
							? 'Inbox is clear'
							: unreadChannels.value > 0
								? `${unreadChannels.value} channel${unreadChannels.value === 1 ? '' : 's'} with unread`
								: 'New messages waiting',
				value: unread ?? '—',
				tone: isAlert ? 'alert' : 'neutral',
				trend: 'none',
				loading: loading.value && unread == null,
			});
		}

		if (hasApp(apps, 'tasks')) {
			const due = tasksDueToday.value;
			const overdue = tasksOverdue.value;
			items.push({
				id: 'tasks-due-today',
				appId: 'tasks',
				icon: 'calendar_today',
				label: 'Pending tasks',
				status:
					due == null
						? 'Loading…'
						: overdue > 0
							? `${overdue} overdue`
							: 'Nothing overdue',
				value: due ?? '—',
				tone: overdue > 0 ? 'alert' : 'neutral',
				trend: 'none',
				loading: loading.value && due == null,
			});

			const completed = completedThisWeek.value;
			const lastWeek = completedLastWeek.value;
			const delta =
				completed != null && lastWeek != null
					? completed - lastWeek
					: null;
			const completedAlert = delta != null && delta < 0;
			items.push({
				id: 'completed-this-week',
				appId: 'tasks',
				icon: 'check_circle',
				label: 'Tasks completed this week',
				status: completed == null ? 'Loading…' : completedStatus(delta),
				value: completed ?? '—',
				tone: completedAlert ? 'alert' : 'neutral',
				trend:
					delta == null || delta === 0
						? 'none'
						: delta > 0
							? 'up'
							: 'down',
				loading: loading.value && completed == null,
			});
		}

		if (hasApp(apps, 'machines')) {
			const needing = machinesNeedingAttention.value;
			const critical = machinesCritical.value;
			const isAlert = (needing ?? 0) > 0 || critical > 0;
			items.push({
				id: 'machines-attention',
				appId: 'machines',
				icon: 'build',
				label: 'Machines with breakdowns',
				status:
					needing == null
						? 'Loading…'
						: critical > 0
							? `${critical} critical fault${critical === 1 ? '' : 's'}`
							: needing > 0
								? 'Needs attention'
								: 'All clear',
				value: needing ?? '—',
				tone: isAlert ? 'alert' : 'neutral',
				trend: isAlert ? 'up' : 'none',
				loading: loading.value && needing == null,
			});
		}

		if (hasApp(apps, 'quality')) {
			const open = openInspections.value;
			const isAlert = (open ?? 0) > 0;
			items.push({
				id: 'open-inspections',
				appId: 'quality',
				icon: 'checklist',
				label: 'In-process inspections',
				status:
					open == null
						? 'Loading…'
						: open === 0
							? 'None open'
							: `${open} open`,
				value: open ?? '—',
				tone: isAlert ? 'alert' : 'neutral',
				trend: 'none',
				loading: loading.value && open == null,
			});
		}

		if (hasApp(apps, 'documents')) {
			const unread = documentsUnread.value;
			const isAlert = (unread ?? 0) > 0;
			items.push({
				id: 'documents-caught-up',
				appId: 'documents',
				icon: 'documents',
				label:
					unread === 0
						? 'Caught up on Documents'
						: 'Unreviewed Documents',
				status:
					unread == null
						? 'Loading…'
						: unread === 0
							? "You're caught up"
							: `${unread} unread`,
				value: unread ?? '—',
				tone: isAlert ? 'alert' : 'neutral',
				trend: 'none',
				loading: loading.value && unread == null,
			});
		}

		return items;
	});

	async function loadChat(): Promise<void> {
		const base = apiBase('VITE_CHAT_API_URL');
		if (!base) {
			unreadMessages.value = 0;
			return;
		}

		const data = await getJson<{ data?: Array<{ unread_count?: number }> }>(
			`${base}/channels?filter[archived]=false`,
		);
		const channels = data?.data ?? [];
		const withUnread = channels.filter((c) => (c.unread_count ?? 0) > 0);
		unreadChannels.value = withUnread.length;
		unreadMessages.value = channels.reduce(
			(sum, c) => sum + (c.unread_count ?? 0),
			0,
		);
	}

	async function loadTasks(): Promise<void> {
		const base = apiBase('VITE_TASKS_API_URL');
		if (!base) {
			tasksDueToday.value = 0;
			completedThisWeek.value = 0;
			completedLastWeek.value = 0;
			return;
		}

		const weekStartDate = startOfWeekMonday(new Date());
		const weekStart = toYmd(weekStartDate);
		const lastWeekStartDate = startOfWeekMonday(
			new Date(weekStartDate.getTime() - 7 * 24 * 60 * 60 * 1000),
		);
		const lastWeekStart = toYmd(lastWeekStartDate);
		const lastWeekEndDate = new Date(weekStartDate);
		lastWeekEndDate.setDate(lastWeekEndDate.getDate() - 1);
		const lastWeekEnd = toYmd(lastWeekEndDate);

		const [home, stale, thisWeek, lastWeek] = await Promise.all([
			getJson<{
				your_tasks?: Array<{ completed_at?: string | null }>;
			}>(`${base}/tasks/home`),
			getJson<{ data?: unknown[] }>(`${base}/tasks/stale`),
			getJson<{ data?: unknown[] }>(
				`${base}/tasks?filter[completed_at_after]=${weekStart}&filter[completed_at_null]=exclude`,
			),
			getJson<{ data?: unknown[] }>(
				`${base}/tasks?filter[completed_at_after]=${lastWeekStart}&filter[completed_at_before]=${lastWeekEnd}&filter[completed_at_null]=exclude`,
			),
		]);

		tasksDueToday.value =
			home?.your_tasks?.filter((task) => !task.completed_at).length ?? 0;
		tasksOverdue.value = stale?.data?.length ?? 0;
		completedThisWeek.value = thisWeek?.data?.length ?? 0;
		completedLastWeek.value = lastWeek?.data?.length ?? 0;
	}

	async function loadMachines(): Promise<void> {
		const base = apiBase('VITE_MACHINES_API_URL');
		if (!base) {
			machinesNeedingAttention.value = 0;
			return;
		}

		const data = await getJson<{
			data?: {
				overdue_tasks?: unknown[];
				recent_breakdowns?: unknown[];
			};
		}>(`${base}/dashboard/summary`);

		const overdue = data?.data?.overdue_tasks?.length ?? 0;
		const breakdowns =
			data?.data?.recent_breakdowns?.filter(
				(b: any) => b.status !== 'completed' && b.status !== 'cancelled',
			).length ?? 0;
		machinesCritical.value = breakdowns;
		machinesNeedingAttention.value = overdue + breakdowns;
	}

	async function loadQuality(): Promise<void> {
		const base = apiBase('VITE_MACHINES_API_URL');
		if (!base) {
			openInspections.value = 0;
			return;
		}

		const data = await getJson<{ data?: unknown[] }>(
			`${base}/inspections?filter[open]=true`,
		);
		openInspections.value = data?.data?.length ?? 0;
	}

	async function loadDocuments(): Promise<void> {
		const base = apiBase('VITE_DOCUMENTS_API_URL');
		if (!base) {
			documentsUnread.value = 0;
			return;
		}

		const data = await getJson<{ data?: { count?: number } }>(
			`${base}/api/v1/notifications/unread-count`,
		);
		documentsUnread.value = data?.data?.count ?? 0;
	}

	async function refresh(): Promise<void> {
		const apps = accessibleApps.value;
		loading.value = true;

		const jobs: Promise<void>[] = [];
		if (hasApp(apps, 'chat')) {
			jobs.push(loadChat());
		}
		if (hasApp(apps, 'tasks')) {
			jobs.push(loadTasks());
		}
		if (hasApp(apps, 'machines')) {
			jobs.push(loadMachines());
		}
		if (hasApp(apps, 'quality')) {
			jobs.push(loadQuality());
		}
		if (hasApp(apps, 'documents')) {
			jobs.push(loadDocuments());
		}

		await Promise.all(jobs);
		loading.value = false;
	}

	onMounted(() => {
		void refresh();
	});

	watch(accessibleApps, () => {
		void refresh();
	});

	return {
		widgets,
		loading,
		refresh,
	};
}
